import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {
  Skeleton,
  SkeletonColorSchemesEnum,
} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {AnalyticsScalarMetricsEnum} from '../../../../models/api/analyticsMetricsModel';
import {getMetricLabels, maybeRenderFeaturePillAndTooltip} from '../../../helpers/metricLabels';
import {AnalyticsEducationTooltip} from '../../reportElements/tooltips/analyticsTooltips';
import {ReportGridItem} from '../../reportViewer/reportGrid';
import {PaddedGridItemDiv, renderMultiSelectDropdown, StyledLabelDiv} from './common';

export const conversationStagesExportColumnsWithTooltips: Record<
  string,
  AnalyticsEducationTooltip | undefined
> = {
  'Stage resolution time': getMetricLabels(AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME).tooltip,
  'Replies to resolution': getMetricLabels(AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION)
    .tooltip,
};

/*
 * Props.
 */

export interface ExportColumnsInputProps {
  availableColumns: readonly string[];
  selectedColumns: readonly string[];
  setColumns: (arg: readonly string[]) => void;
  isLoading?: boolean;
}

/*
 * Styles.
 */

const StyledColumnWithPillDiv = styled.div`
  ${flexCenter};
  gap: 4px;
`;

const StyledHeaderLabelDiv = styled.div`
  ${flexCenter}
  gap: 4px;
`;

/*
 * Component.
 */

export const ExportColumnsInput: FC<ExportColumnsInputProps> = ({
  availableColumns,
  selectedColumns,
  setColumns,
  isLoading,
}) => {
  if (isLoading) {
    return renderLoadingState();
  }

  return (
    <PaddedGridItemDiv>
      {renderLabel()}

      <DropdownBoxCoordinator
        shouldDisablePadding
        value={renderColumnsHeader(availableColumns.length, selectedColumns.length)}
        renderDropdown={(popoverProps) =>
          renderMultiSelectDropdown(popoverProps, availableColumns, selectedColumns, setColumns, renderColumn)
        }
      />
    </PaddedGridItemDiv>
  );
};

/*
 * Helpers.
 */

function renderLabel() {
  return (
    <StyledLabelDiv>
      <Trans id="export-confirmation-label-columns">Include fields</Trans>
    </StyledLabelDiv>
  );
}

function renderColumnsHeader(availableColumnsCount: number, selectedColumnsCount: number) {
  const headerLabel =
    selectedColumnsCount === availableColumnsCount ? (
      <Trans id="analytics-export-confirmation-select-all-fields-header">All fields</Trans>
    ) : (
      <Plural
        id="analytics-export-confirmation-select-columns-header"
        value={selectedColumnsCount}
        one="1 column"
        other="# columns"
      />
    );

  return <StyledHeaderLabelDiv>{headerLabel}</StyledHeaderLabelDiv>;
}

function renderLoadingState() {
  return (
    <PaddedGridItemDiv>
      {renderLabel()}
      <ReportGridItem>
        <Skeleton width="100%" height="26px" borderRadius="6px" scheme={SkeletonColorSchemesEnum.GREY10} />
      </ReportGridItem>
    </PaddedGridItemDiv>
  );
}

function renderColumn(column: string) {
  const columnWithTooltips = conversationStagesExportColumnsWithTooltips[column];

  if (!columnWithTooltips) {
    return column;
  }

  return (
    <StyledColumnWithPillDiv>
      {column} {maybeRenderFeaturePillAndTooltip(false, true, columnWithTooltips)}
    </StyledColumnWithPillDiv>
  );
}
